import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Handover.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const getUser = () => localStorage.getItem('notes_user') || '';

const apiFetch = async (url, opts = {}) => {
  const token = getToken();
  if (token && !opts.headers) opts.headers = {};
  if (token) opts.headers['x-token'] = token;
  const res = await fetch(url, opts);
  if (res.status === 401) {
    localStorage.removeItem('notes_token');
    localStorage.removeItem('notes_user');
    alert('登录超时,请重新登录');
    window.location.href = '/';
    return null;
  }
  return res;
};

// 附件上传组件
function FileUpload({ files = [], onChange, multiple = true, id }) {
  const uploadId = id || `file-upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const handleUpload = async (e) => {
    const uploadedFiles = Array.from(e.target.files);
    const newFiles = [];

    for (const file of uploadedFiles) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await apiFetch(`${API_URL}/api/upload`, {
          method: 'POST',
          body: formData
        });
        const data = await res.json();
        if (data.success) {
          newFiles.push({
            name: file.name,
            url: data.url,
            size: file.size
          });
        }
      } catch (err) {
        console.error('上传失败:', err);
      }
    }

    if (multiple) {
      onChange([...files, ...newFiles]);
    } else {
      onChange(newFiles);
    }
  };

  const handleRemove = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    onChange(newFiles);
  };

  return (
    <div className="file-upload">
      <input
        type="file"
        multiple={multiple}
        onChange={handleUpload}
        style={{ display: 'none' }}
        id={uploadId}
      />
      <label htmlFor={uploadId} className="upload-btn">
        📎 选择文件
      </label>
      {files.length > 0 && (
        <div className="file-list">
          {files.map((file, idx) => (
            <div key={idx} className="file-item">
              <span className="file-name">{file.name}</span>
              <button onClick={() => handleRemove(idx)} className="remove-btn">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// 动态表格组件
function DynamicTable({ items = [], onChange }) {
  const addItem = () => {
    onChange([...items, { item: '', steps: '', notes: '' }]);
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    onChange(newItems);
  };

  return (
    <div className="dynamic-table">
      <table>
        <thead>
          <tr>
            <th style={{ width: '25%' }}>运维事项</th>
            <th style={{ width: '40%' }}>操作步骤说明</th>
            <th style={{ width: '30%' }}>注意事项及异常处理</th>
            <th style={{ width: '5%' }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={idx}>
              <td>
                <textarea
                  value={item.item}
                  onChange={(e) => updateItem(idx, 'item', e.target.value)}
                  rows={2}
                  placeholder="输入运维事项"
                />
              </td>
              <td>
                <textarea
                  value={item.steps}
                  onChange={(e) => updateItem(idx, 'steps', e.target.value)}
                  rows={2}
                  placeholder="输入操作步骤"
                />
              </td>
              <td>
                <textarea
                  value={item.notes}
                  onChange={(e) => updateItem(idx, 'notes', e.target.value)}
                  rows={2}
                  placeholder="输入注意事项"
                />
              </td>
              <td>
                <button onClick={() => removeItem(idx)} className="remove-row-btn">×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addItem} className="add-row-btn">+ 添加行</button>
    </div>
  );
}

// 发起交接表单
function HandoverForm({ users, initialData, onSubmit, onCancel, isEdit }) {
  const currentUser = getUser();
  const [formData, setFormData] = useState({
    projectName: initialData?.projectName || '',
    handoverDate: initialData?.handoverDate || new Date().toISOString().split('T')[0],
    receiveBy: initialData?.receiveBy || '',
    blueprintFiles: initialData?.blueprintFiles || [],
    manualFiles: initialData?.manualFiles || [],
    manualNote: initialData?.manualNote || '',
    operationItems: initialData?.operationItems || [{ item: '', steps: '', notes: '' }],
    permissionSelfManage: initialData?.permissionSelfManage || { checked: false, note: '' },
    hotIssueFiles: initialData?.hotIssueFiles || [],
    maintenanceFiles: initialData?.maintenanceFiles || []
  });

  const handleSubmit = () => {
    if (!formData.projectName) {
      alert('请输入项目名称');
      return;
    }
    if (!formData.receiveBy) {
      alert('请选择接收人');
      return;
    }
    if (!formData.blueprintFiles || formData.blueprintFiles.length === 0) {
      alert('请上传项目蓝图方案附件');
      return;
    }
    if (!formData.manualFiles || formData.manualFiles.length === 0) {
      alert('请上传操作手册附件');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="handover-form">
      {/* 固定顶部栏 */}
      <div className="form-sticky-header">
        <div className="warning-box">
          ⚠️ 项目交接所提交的所有附件,不可包含敏感信息,包括登录地址、用户及密码、服务器信息等,请检查后上传,敏感信息传递,请线下发送接收人!!!
        </div>
        <div className="form-actions-top">
          <button onClick={handleSubmit} className="btn-primary">
            {isEdit ? '重新提交' : '提交'}
          </button>
          <button onClick={onCancel} className="btn-secondary">取消</button>
        </div>
      </div>

      {/* 基本信息 */}
      <div className="form-section">
        <h3>📋 基本信息</h3>
        <div className="form-row">
          <div className="form-field">
            <label>项目名称 <span className="required">*</span></label>
            <input
              type="text"
              value={formData.projectName}
              onChange={(e) => setFormData({...formData, projectName: e.target.value})}
              maxLength={100}
              placeholder="请输入项目名称(最多100字)"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label>交接人</label>
            <input type="text" value={users.find(u => u.username === currentUser)?.nickname || currentUser} disabled />
          </div>
          <div className="form-field">
            <label>接收人 <span className="required">*</span></label>
            <select
              value={formData.receiveBy}
              onChange={(e) => setFormData({...formData, receiveBy: e.target.value})}
            >
              <option value="">请选择接收人</option>
              {users.filter(u => u.username !== currentUser).map(u => (
                <option key={u.username} value={u.username}>{u.nickname}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>交接日期</label>
            <input
              type="date"
              value={formData.handoverDate}
              onChange={(e) => setFormData({...formData, handoverDate: e.target.value})}
            />
          </div>
        </div>
      </div>

      {/* 项目蓝图方案 */}
      <div className="form-section">
        <h3>📐 项目蓝图方案 <span className="required">*</span></h3>
        <div className="hint-box">
          📌 提交项目最新定稿蓝图方案,包含整体规划、架构设计、功能方案、落地细则及迭代记录,严禁使用旧版、过渡版文件,保证与项目实际落地情况一致
        </div>
        <FileUpload
          id="blueprint-upload"
          files={formData.blueprintFiles}
          onChange={(files) => setFormData({...formData, blueprintFiles: files})}
        />
      </div>

      {/* 项目对接人 */}
      <div className="form-section">
        <h3>👥 项目对接人</h3>
        <div className="info-box">
          i️ 联系人及联系方式,请线下告知接收人!
        </div>
      </div>

      {/* 操作手册 */}
      <div className="form-section">
        <h3>📖 操作手册 <span className="required">*</span></h3>

        <div className="subsection">
          <label>📎 操作手册附件</label>
          <FileUpload
            id="manual-upload"
            files={formData.manualFiles || []}
            onChange={(files) => setFormData({...formData, manualFiles: files})}
          />
          <textarea
            className="full-width-textarea"
            value={formData.manualNote || ''}
            onChange={(e) => setFormData({...formData, manualNote: e.target.value})}
            placeholder="补充说明(可选)"
            rows={2}
          />
        </div>

        <div className="subsection">
          <label className="bold-label">⚙️ 用户自主运维事项说明</label>
          <div className="hint-text">
            列明需用户管理员自主运维的相关事项,标注操作步骤、注意事项及异常处理方式,明确双方权责。
          </div>
          <DynamicTable
            items={formData.operationItems}
            onChange={(items) => setFormData({...formData, operationItems: items})}
          />
        </div>

        <div className="subsection">
          <label className="checkbox-label">
            🔐 权限管理是否由用户自主运维
            <input
              type="checkbox"
              checked={formData.permissionSelfManage.checked}
              onChange={(e) => setFormData({
                ...formData,
                permissionSelfManage: { ...formData.permissionSelfManage, checked: e.target.checked }
              })}
            />
          </label>
          {formData.permissionSelfManage.checked && (
            <textarea
              className="full-width-textarea"
              value={formData.permissionSelfManage.note}
              onChange={(e) => setFormData({
                ...formData,
                permissionSelfManage: { ...formData.permissionSelfManage, note: e.target.value }
              })}
              placeholder="请说明权限管理方式"
              rows={3}
            />
          )}
        </div>
      </div>

      {/* 运维手册 */}
      <div className="form-section">
        <h3>🛠️ 运维手册</h3>

        <div className="subsection">
          <label>🔥 热点/高频问题解决方案</label>
          <FileUpload
            id="hot-issue-upload"
            files={formData.hotIssueFiles}
            onChange={(files) => setFormData({...formData, hotIssueFiles: files})}
          />
          <textarea
            className="full-width-textarea"
            value={formData.hotIssueFiles[0]?.note || ''}
            onChange={(e) => {
              const files = [...formData.hotIssueFiles];
              if (files.length > 0) {
                files[0] = { ...files[0], note: e.target.value };
              }
              setFormData({...formData, hotIssueFiles: files});
            }}
            placeholder="补充说明(可选)"
            rows={2}
          />
        </div>

        <div className="subsection">
          <label>🔧 系统维护或其他专项操作指南</label>
          <FileUpload
            id="maintenance-upload"
            files={formData.maintenanceFiles}
            onChange={(files) => setFormData({...formData, maintenanceFiles: files})}
          />
          <textarea
            className="full-width-textarea"
            value={formData.maintenanceFiles[0]?.note || ''}
            onChange={(e) => {
              const files = [...formData.maintenanceFiles];
              if (files.length > 0) {
                files[0] = { ...files[0], note: e.target.value };
              }
              setFormData({...formData, maintenanceFiles: files});
            }}
            placeholder="补充说明(可选)"
            rows={2}
          />
        </div>
      </div>
    </div>
  );
}

// 项目详情
function HandoverDetail({ handover, users, onBack, onAccept, onReject, isReceiver }) {
  const handleDownload = (url, filename) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  return (
    <div className="handover-detail">
      <button onClick={onBack} className="back-btn">← 返回列表</button>

      {handover.status === 'rejected' && (
        <div className="reject-notice">
          ⚠️ 该交接已被驳回,驳回原因:{handover.rejectReason || '未说明'}
        </div>
      )}

      <div className="detail-header">
        <h2>{handover.projectName}</h2>
        <div className="meta-info">
          <span>交接人:{users.find(u => u.username === handover.handoverBy)?.nickname || handover.handoverBy}</span>
          <span>接收人:{users.find(u => u.username === handover.receiveBy)?.nickname || handover.receiveBy}</span>
          <span>交接日期:{new Date(handover.submitTime).toLocaleDateString()}</span>
          <span className={`status-badge status-${handover.status}`}>
            {handover.status === 'submitted' && '待接收'}
            {handover.status === 'accepted' && '已接收'}
            {handover.status === 'rejected' && '已驳回'}
          </span>
        </div>
      </div>

      {/* 项目蓝图方案 */}
      <div className="detail-section">
        <h3>项目蓝图方案</h3>
        <div className="subsection">
          {handover.blueprintFiles.length > 0 ? (
            <div className="file-list">
              {handover.blueprintFiles.map((file, idx) => (
                <div key={idx} className="file-item">
                  <span>📄 {file.name}</span>
                  <button onClick={() => handleDownload(file.url, file.name)}>下载</button>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-text">暂无附件</p>
          )}
        </div>
      </div>

      {/* 项目对接人 */}
      <div className="detail-section">
        <h3>项目对接人</h3>
        <div className="subsection">
          <p className="info-text">💡 联系人及联系方式,请线下告知接收人!</p>
        </div>
      </div>

      {/* 操作手册 */}
      <div className="detail-section">
        <h3>操作手册</h3>

        <div className="subsection">
          <h4>📋 操作手册附件</h4>
          {handover.manualFiles && handover.manualFiles.length > 0 ? (
            <>
              <div className="file-list">
                {handover.manualFiles.map((file, idx) => (
                  <div key={idx} className="file-item">
                    <span>📄 {file.name}</span>
                    <button onClick={() => handleDownload(file.url, file.name)}>下载</button>
                  </div>
                ))}
              </div>
              {handover.manualNote && (
                <p className="note-text">📝 {handover.manualNote}</p>
              )}
            </>
          ) : (
            <p className="empty-text">暂无附件</p>
          )}
        </div>

        <div className="subsection">
          <h4>⚙️ 用户自主运维事项说明</h4>
          {handover.operationItems.length > 0 ? (
            <table className="detail-table">
              <thead>
                <tr>
                  <th>运维事项</th>
                  <th>操作步骤说明</th>
                  <th>注意事项及异常处理</th>
                </tr>
              </thead>
              <tbody>
                {handover.operationItems.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.item}</td>
                    <td>{item.steps}</td>
                    <td>{item.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="empty-text">暂无数据</p>
          )}
        </div>

        <div className="subsection">
          <h4>🔐 权限管理是否由用户自主运维</h4>
          <p style={{ margin: 0 }}>
            <span style={{
              display: 'inline-block',
              padding: '2px 10px',
              borderRadius: '4px',
              fontSize: '13px',
              background: handover.permissionSelfManage?.checked ? '#f6ffed' : '#f5f5f5',
              color: handover.permissionSelfManage?.checked ? '#52c41a' : '#999',
              border: `1px solid ${handover.permissionSelfManage?.checked ? '#b7eb8f' : '#e8e8e8'}`
            }}>
              {handover.permissionSelfManage?.checked ? '是' : '否'}
            </span>
          </p>
          {handover.permissionSelfManage?.note && (
            <p className="note-text">📝 {handover.permissionSelfManage.note}</p>
          )}
        </div>
      </div>

      {/* 运维手册 */}
      <div className="detail-section">
        <h3>运维手册</h3>

        <div className="subsection">
          <h4>🔥 热点/高频问题解决方案</h4>
          {handover.hotIssueFiles.length > 0 ? (
            <>
              <div className="file-list">
                {handover.hotIssueFiles.map((file, idx) => (
                  <div key={idx} className="file-item">
                    <span>📄 {file.name}</span>
                    <button onClick={() => handleDownload(file.url, file.name)}>下载</button>
                  </div>
                ))}
              </div>
              {handover.hotIssueFiles[0]?.note && (
                <p className="note-text">📝 {handover.hotIssueFiles[0].note}</p>
              )}
            </>
          ) : (
            <p className="empty-text">暂无附件</p>
          )}
        </div>

        <div className="subsection">
          <h4>🛠️ 系统维护或其他专项操作指南</h4>
          {handover.maintenanceFiles.length > 0 ? (
            <>
              <div className="file-list">
                {handover.maintenanceFiles.map((file, idx) => (
                  <div key={idx} className="file-item">
                    <span>📄 {file.name}</span>
                    <button onClick={() => handleDownload(file.url, file.name)}>下载</button>
                  </div>
                ))}
              </div>
              {handover.maintenanceFiles[0]?.note && (
                <p className="note-text">📝 {handover.maintenanceFiles[0].note}</p>
              )}
            </>
          ) : (
            <p className="empty-text">暂无附件</p>
          )}
        </div>
      </div>

      {/* 操作按钮 */}
      {isReceiver && handover.status === 'submitted' && (
        <div className="detail-actions">
          <button onClick={onAccept} className="btn-primary">确认接收</button>
          <button onClick={onReject} className="btn-danger">驳回</button>
        </div>
      )}
    </div>
  );
}

// 主组件
export default function Handover() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('initiate'); // initiate, receive
  const [users, setUsers] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentHandover, setCurrentHandover] = useState(null);
  const [editingHandover, setEditingHandover] = useState(null);
  const [showForm, setShowForm] = useState(false); // 是否显示表单
  const [statusFilter, setStatusFilter] = useState('all');
  const [keyword, setKeyword] = useState('');

  const currentUser = getUser();
  const isAdmin = ['jch', 'gary', 'lizhigang'].includes(currentUser);

  useEffect(() => {
    loadData();
    // 检查是否有待办跳转
    const handoverId = searchParams.get('id');
    if (handoverId) {
      loadDetail(handoverId);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'receive' || activeTab === 'initiate') {
      loadList();
    }
  }, [activeTab, statusFilter, keyword]);

  const loadData = async () => {
    try {
      const res = await apiFetch(`${API_URL}/api/handover/users`);
      const data = await res.json();
      if (data.success) {
        setUsers(data.data);
      }
    } catch (e) {
      console.error('加载用户列表失败:', e);
    }
    setLoading(false);
  };

  const loadList = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (keyword) params.append('keyword', keyword);
      // 传递当前 tab 类型给后端
      if (activeTab === 'initiate' || activeTab === 'receive') {
        params.append('type', activeTab);
      }

      const res = await apiFetch(`${API_URL}/api/handover/list?${params}`);
      const data = await res.json();
      if (data.success) {
        setHandovers(data.data);
      }
    } catch (e) {
      console.error('加载列表失败:', e);
    }
  };

  const loadDetail = async (id) => {
    try {
      const res = await apiFetch(`${API_URL}/api/handover/${id}`);
      const data = await res.json();
      if (data.success) {
        setCurrentHandover(data.data);
      }
    } catch (e) {
      console.error('加载详情失败:', e);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      const res = await apiFetch(`${API_URL}/api/handover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        alert('提交成功!');
        setEditingHandover(null);
        setShowForm(false);
        loadList();
      } else {
        alert('提交失败:' + data.error);
      }
    } catch (e) {
      console.error('提交失败:', e);
      alert('提交失败');
    }
  };

  const handleUpdate = async (formData) => {
    try {
      const res = await apiFetch(`${API_URL}/api/handover/${editingHandover.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        alert('重新提交成功!');
        setEditingHandover(null);
        loadList();
      } else {
        alert('提交失败:' + data.error);
      }
    } catch (e) {
      console.error('提交失败:', e);
      alert('提交失败');
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('确认删除该交接申请?')) return;
    try {
      const res = await apiFetch(`${API_URL}/api/handover/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        loadList();
      } else {
        alert('删除失败:' + data.error);
      }
    } catch (e) {
      console.error('删除失败:', e);
      alert('删除失败');
    }
  };

  const handleAccept = async () => {
    if (!window.confirm('确认接收该项目交接?')) return;
    try {
      const res = await apiFetch(`${API_URL}/api/handover/${currentHandover.id}/accept`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        alert('已确认接收!');
        setCurrentHandover(null);
        loadList();
      } else {
        alert('操作失败:' + data.error);
      }
    } catch (e) {
      console.error('操作失败:', e);
      alert('操作失败');
    }
  };

  const handleReject = async () => {
    const reason = prompt('请输入驳回原因:');
    if (reason === null) return;
    try {
      const res = await apiFetch(`${API_URL}/api/handover/${currentHandover.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      const data = await res.json();
      if (data.success) {
        alert('已驳回!');
        setCurrentHandover(null);
        loadList();
      } else {
        alert('操作失败:' + data.error);
      }
    } catch (e) {
      console.error('操作失败:', e);
      alert('操作失败');
    }
  };

  if (loading) {
    return <div className="handover-container loading">加载中...</div>;
  }

  // 详情页
  if (currentHandover) {
    const isReceiver = currentHandover.receiveBy === currentUser;
    return (
      <div className="handover-container">
        <HandoverDetail
          handover={currentHandover}
          users={users}
          onBack={() => setCurrentHandover(null)}
          onAccept={handleAccept}
          onReject={handleReject}
          isReceiver={isReceiver}
        />
      </div>
    );
  }

  // 编辑页
  if (editingHandover) {
    return (
      <div className="handover-container">
        <HandoverForm
          users={users}
          initialData={editingHandover}
          onSubmit={handleUpdate}
          onCancel={() => { setEditingHandover(null); setShowForm(false); }}
          isEdit={true}
        />
      </div>
    );
  }

  // 发起交接 - 显示表单
  if (activeTab === 'initiate' && showForm) {
    return (
      <div className="handover-container">
        <HandoverForm
          users={users}
          onSubmit={handleSubmit}
          onCancel={() => { setShowForm(false); loadList(); }}
          isEdit={false}
        />
      </div>
    );
  }

  // 发起交接 - 显示列表
  if (activeTab === 'initiate') {
    const myHandovers = handovers.filter(h => h.handoverBy === currentUser);
    const rejectedItems = myHandovers.filter(h => h.status === 'rejected');
    const otherItems = myHandovers.filter(h => h.status !== 'rejected');
    
    return (
      <div className="handover-container">
        <div className="tab-header">
          <button
            className={activeTab === 'initiate' ? 'active' : ''}
            onClick={() => setActiveTab('initiate')}
          >
            发起交接
          </button>
          <button
            className={activeTab === 'receive' ? 'active' : ''}
            onClick={() => setActiveTab('receive')}
          >
            接收项目
          </button>
        </div>
        
        <div className="list-header">
          <h3>我发起的交接</h3>
          <button className="btn-primary" onClick={() => setShowForm(true)}>+ 新增交接</button>
        </div>
        
        {/* 被驳回的项目 */}
        {rejectedItems.length > 0 && (
          <div className="rejected-section">
            <h4>被驳回的交接（可重新编辑）</h4>
            {rejectedItems.map(item => (
              <div key={item.id} className="rejected-card">
                <span className="project-name">{item.projectName}</span>
                <span>接收人：{users.find(u => u.username === item.receiveBy)?.nickname || item.receiveBy}</span>
                <span className="reject-reason">驳回原因：{item.rejectReason || '未说明'}</span>
                <button onClick={() => { setEditingHandover(item); setShowForm(true); }}>重新编辑</button>
              </div>
            ))}
          </div>
        )}
        
        {/* 交接列表 */}
        <div className="handover-list">
          {otherItems.length === 0 ? (
            <div className="empty-state">暂无发起的交接记录</div>
          ) : (
            otherItems.map(item => (
              <div
                key={item.id}
                className="handover-card"
                onClick={() => loadDetail(item.id)}
              >
                <div className="card-header">
                  <h4>{item.projectName}</h4>
                  <span className={`status-badge status-${item.status}`}>
                    {item.status === 'submitted' && '待接收'}
                    {item.status === 'accepted' && '已接收'}
                  </span>
                </div>
                <div className="card-meta">
                  <span>接收人：{users.find(u => u.username === item.receiveBy)?.nickname || item.receiveBy}</span>
                  <span>日期：{new Date(item.submitTime).toLocaleDateString()}</span>
                  {item.status === 'submitted' && (
                    <button className="delete-btn" onClick={(e) => handleDelete(item.id, e)}>删除</button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // 接收项目列表
  return (
    <div className="handover-container">
      <div className="tab-header">
        <button
          className={activeTab === 'initiate' ? 'active' : ''}
          onClick={() => setActiveTab('initiate')}
        >
          发起交接
        </button>
        <button
          className={activeTab === 'receive' ? 'active' : ''}
          onClick={() => setActiveTab('receive')}
        >
          接收项目
        </button>
      </div>

      <div className="filter-bar">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">全部</option>
          <option value="submitted">待接收</option>
          <option value="accepted">已接收</option>
          <option value="rejected">已驳回</option>
        </select>
        <input
          type="text"
          placeholder="搜索项目名称、交接人、接收人..."
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
      </div>

      <div className="handover-list">
        {handovers.length === 0 ? (
          <div className="empty-state">暂无数据</div>
        ) : (
          handovers.map(item => (
            <div
              key={item.id}
              className="handover-card"
              onClick={() => loadDetail(item.id)}
            >
              <div className="card-header">
                <h4>{item.projectName}</h4>
                <span className={`status-badge status-${item.status}`}>
                  {item.status === 'submitted' && '待接收'}
                  {item.status === 'accepted' && '已接收'}
                  {item.status === 'rejected' && '已驳回'}
                </span>
              </div>
              <div className="card-meta">
                <span>交接人:{users.find(u => u.username === item.handoverBy)?.nickname || item.handoverBy}</span>
                <span>接收人:{users.find(u => u.username === item.receiveBy)?.nickname || item.receiveBy}</span>
                <span>日期:{new Date(item.submitTime).toLocaleDateString()}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
